import { useMemo, useState } from 'react';

import { ROUTES } from '@/lib/routes';
import { SessionStatus } from '@/lib/types/session-status';
import { formatTimeWithTimeZone } from '@/lib/utils/format-time-with-timezone';
import { SessionHub } from '@addmile/backend-client';
import { CellContext } from '@tanstack/react-table';

import { StatusSelect } from '../../StatusSelect/StatusSelect';
import { useStatusChange } from '../../StatusSelect/useStatusChange';
import Button from '../../shared/Button/Button';
import { useColumnsVisibility } from './useColumnsVisibility';

export const useCoachSessionsTableData = (sessions: SessionHub[]) => {
  const { selectedStatus, handleStatusChange } = useStatusChange(sessions);
  const [statusFilter, setStatusFilter] = useState<string | null>(SessionStatus.Booked);

  const filteredSessions = useMemo(() => {
    if (!statusFilter) return sessions;

    return sessions.filter(
      (session) => (selectedStatus[session.id] || session.status) === statusFilter,
    );
  }, [sessions, selectedStatus, statusFilter]);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'id',
        header: '#',
        cell: ({ row }: CellContext<SessionHub, React.ReactNode>) => <span>{row.index + 1}</span>,
        size: 10,
      },
      {
        accessorKey: 'startsAt',
        header: 'Date',
        cell: ({ getValue, row }: CellContext<SessionHub, string>) => {
          const timezone = row.original.coach?.timezone;

          return (
            <span>
              {typeof getValue() === 'string'
                ? formatTimeWithTimeZone(getValue(), timezone).dateOnly
                : getValue()}
            </span>
          );
        },
        size: 100,
      },
      {
        id: 'time',
        accessorKey: 'startsAt',
        header: 'Time',
        cell: ({ getValue, row }: CellContext<SessionHub, string>) => {
          const timezone = row.original.coach?.timezone;
          return typeof getValue() === 'string'
            ? formatTimeWithTimeZone(getValue(), timezone).time
            : getValue();
        },
        size: 80,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 100,
        cell: ({ row }: CellContext<SessionHub, string>) => {
          const status = selectedStatus[row.original.id] || row.original.status;
          return (
            <StatusSelect
              status={status}
              onChange={(value) => handleStatusChange(row.original.id, value)}
            />
          );
        },
      },
      {
        accessorKey: 'bookedAt',
        header: 'Request Date',
        size: 100,
        cell: ({ getValue, row }: CellContext<SessionHub, string>) => {
          const timezone = row.original.coach?.timezone;
          return typeof getValue() === 'string'
            ? formatTimeWithTimeZone(getValue(), timezone).date
            : getValue();
        },
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }: CellContext<SessionHub, string>) => {
          const sessionLink = `${ROUTES.ADMIN_SESSIONS}/${row.original.id}?coachId=${row.original.coach?.id}`;

          return (
            <Button
              size="s"
              variant="transparent"
              className="w-[112px]"
              onClick={() => window.open(sessionLink, '_blank')}
              label="Details"
            />
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleStatusChange, selectedStatus],
  );

  const { columnVisibility, toggleColumnVisibility, columnOptions } = useColumnsVisibility({
    columnsKeys: {
      id: true,
      startsAt: true,
      time: true,
    },
    columns,
  });

  return {
    data: filteredSessions,
    columns,
    statusFilter,
    setStatusFilter,
    columnVisibility,
    toggleColumnVisibility,
    columnOptions,
  };
};
